/**
 * Make citeproc print the style's own "et al." for a reference whose author list was cut short.
 *
 * The converter marks such a record with CSL `custom.etAl = true` and keeps only the names the
 * source printed. citeproc 2.4.63 has no data convention for that: it counts the names it is
 * given, so a short list never reaches the style's et-al threshold, and a placeholder name such
 * as {literal: 'others'} prints "others". Here the flag is turned into one more name than the
 * item holds, so citeproc applies the style's own et-al-min / et-al-use-first, the delimiter
 * before "et al." and the locale's et-al term, exactly as for a list that was long to begin with.
 *
 * Both hooks wrap citeproc INTERNALS (`CSL.NameOutput.prototype`), not public API. They read
 * `this.Item`, `this.freeters_count` and `this.etal_use_last`. A citeproc upgrade can rename or
 * restructure any of them, which would silently print the short list without "et al.". The spec
 * `cut-short-et-al.spec.ts` pins the citeproc version and fails on a change, so whoever upgrades
 * re-checks both hooks. An item without the flag takes the original code path unchanged.
 */

/**
 * Is this name list the author list of a flagged item? The flag says the AUTHORS were cut short,
 * so a names node that renders other variables only (editor, translator, ...) must not be touched.
 * `variables` is the list of CSL variables the current <names> node renders (set in NameOutput.init).
 */
const isCutShortAuthorList = (nameOutput: any): boolean =>
  nameOutput?.Item?.custom?.etAl === true
  && Array.isArray(nameOutput.variables)
  && nameOutput.variables.indexOf('author') > -1;

const INSTALLED = '__sfCutShortEtAl';

/** Idempotent: wraps the two citeproc methods once per CSL module. */
export const installCutShortEtAl = (CSL: any): void => {
    const proto = CSL?.NameOutput?.prototype;
    if (!proto || proto[INSTALLED]) { return; }
    proto[INSTALLED] = true;

    // citeproc runs getEtAlConfig() and then truncatePersonalNameLists(); the et-al decision
    // (_imposeNameConstraints) happens later and compares the name count with the style's threshold.
    const originalTruncate = proto.truncatePersonalNameLists;
    proto.truncatePersonalNameLists = function (...args: any[]) {
        const result = originalTruncate.apply(this, args);
        // truncatePersonalNameLists records the ORIGINAL number of names in freeters_count and
        // the et-al evaluation reads that count. One more than we hold makes the list "too long"
        // wherever the style's threshold says so, without adding a printable name.
        // The count guard is defensive: for an author-less item the names node is never reached with
        // an author list, so it does not change today's output. Kept so a flagged item with no
        // authors can never be given a phantom name after a citeproc change.
        if (isCutShortAuthorList(this) && this.freeters_count && this.freeters_count.author > 0) {
            this.freeters_count.author += 1;
        }
        return result;
    };

    const originalEtAlConfig = proto.getEtAlConfig;
    proto.getEtAlConfig = function (...args: any[]) {
        const result = originalEtAlConfig.apply(this, args);
        // et-al-use-last (some APA-like styles) would print "… <last name>" and take the final
        // name we hold as if it were the real last author. Ours is not: the real last is unknown.
        if (isCutShortAuthorList(this)) { this.etal_use_last = false; }
        return result;
    };
};
